/**
 * Sleep for a given interval (in ms)
 * @param ms number of milliseconds to sleep
 * @returns a promise which resolves after the specified interval
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

/**
 * Get time in milliseconds from a timestamp string
 * @param timestamp an ISO timestamp string
 * @returns the time of the timestamp in ms
 */
export function getTime(timestamp: string): number {
  return new Date(timestamp).getTime();
}

/**
 * CET timezone identifier (Europe/Amsterdam, handles CET/CEST DST automatically)
 */
export const CET_TIMEZONE = 'Europe/Amsterdam';

/**
 * Check if a timestamp is before the last midnight in CET/CEST (Europe/Amsterdam).
 *
 * Compares the Amsterdam calendar date of the timestamp against today's Amsterdam
 * calendar date — if they differ the timestamp is from a previous day, i.e. before
 * last midnight CET.
 *
 * @param timestampMillis - The timestamp to check in milliseconds
 * @param currentTimeMillis - Current time in milliseconds (defaults to Date.now())
 * @returns true if the timestamp is before the last midnight CET, false otherwise
 */
export function isBeforeLastMidnightCET(
  timestampMillis: number,
  currentTimeMillis: number = Date.now()
): boolean {
  // 'sv' locale formats as "YYYY-MM-DD", which is lexicographically sortable.
  // Intl handles CET (+01:00) vs CEST (+02:00) DST transitions automatically
  // via the IANA timezone name — no manual offset parsing needed.
  const fmt = new Intl.DateTimeFormat('sv', {timeZone: CET_TIMEZONE});
  return fmt.format(timestampMillis) < fmt.format(currentTimeMillis);
}
